import React, { useState, useEffect } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription } from './components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from './components/ui/alert-dialog';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Settings, DollarSign, Palette, Printer, Lock, Save, Plus, Trash2, Eye, EyeOff, ClipboardList } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { axiosWithRetry } from './utils/withRetry';

const API = `/api`;

function AdminLogin({ onLogin }) {
  const [loginMethod, setLoginMethod] = useState('password');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    apiKey: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let response;
      
      if (loginMethod === 'password') {
        response = await axiosWithRetry({
          method: 'post', url: `${API}/admin/login`,
          data: { username: formData.username, password: formData.password }
        });
      } else {
        response = await axiosWithRetry({
          method: 'post', url: `${API}/admin/login-api-key`,
          data: { username: formData.username, api_key: formData.apiKey }
        });
      }
      
      if (response.data.success) {
        sessionStorage.setItem('adminToken', response.data.token);
        sessionStorage.setItem('adminUserInfo', JSON.stringify(response.data.user_info));
        onLogin(response.data.token, response.data.user_info);
        toast.success(`Erfolgreich angemeldet als ${response.data.user_info?.name || formData.username}`);
      } else {
        toast.error(response.data.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Login error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center gap-2 justify-center">
            <Lock className="w-6 h-6 text-red-600" />
            Odoo Admin-Zugang
          </CardTitle>
          <CardDescription className="text-gray-700">
            Nur für autorisierte Odoo-Administratoren<br />
            <span className="text-xs text-gray-500">3dfabrikshop.de</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Login Method Selector */}
          <div className="mb-4">
            <Label className="text-sm font-medium">Anmeldemethode</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant={loginMethod === 'password' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLoginMethod('password')}
                className="flex-1"
              >
                Passwort
              </Button>
              <Button
                type="button"
                variant={loginMethod === 'api_key' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLoginMethod('api_key')}
                className="flex-1"
              >
                API-Schlüssel
              </Button>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Username Field */}
            <div>
              <Label htmlFor="username">Benutzername</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                placeholder="Ihr Odoo-Benutzername"
                required
              />
            </div>

            {/* Password or API Key Field */}
            {loginMethod === 'password' ? (
              <div>
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="pr-10"
                    placeholder="Ihr Odoo-Passwort"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="apiKey">API-Schlüssel</Label>
                <div className="relative">
                  <Input
                    id="apiKey"
                    type={showApiKey ? 'text' : 'password'}
                    value={formData.apiKey}
                    onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
                    className="pr-10"
                    placeholder="Ihr Odoo-API-Schlüssel"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  API-Schlüssel können in Ihrem Odoo-Profil generiert werden
                </div>
              </div>
            )}

            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={isLoading}>
              {isLoading ? 'Authentifizierung...' : 'Anmelden'}
            </Button>
          </form>

          {/* Help Text */}
          <div className="mt-4 text-xs text-gray-500 text-center">
            <p>Sie benötigen eine der folgenden Administrator-Berechtigungen:</p>
            <p className="text-xs mt-1">Dashboard, Einkauf, Fertigung, Lager, Verkauf,<br />Livechat, Buchhaltung oder Vorformulierte Antworten</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MaterialsManager({ materials, onUpdateMaterials }) {
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newMaterial, setNewMaterial] = useState({
    type: 'filament',
    key: '',
    name: '',
    density: 1.0,
    price_per_gram: 0.1,
    colors: []
  });

  const handleSaveMaterial = async (materialType, materialKey, updatedMaterial) => {
    try {
      await axiosWithRetry({
        method: 'put', url: `${API}/admin/materials/${materialType}/${materialKey}`,
        data: updatedMaterial,
        headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      toast.success('Material aktualisiert');
      onUpdateMaterials();
      setEditingMaterial(null);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteMaterial = (materialType, materialKey, materialName) => {
    setDeleteConfirm({ materialType, materialKey, materialName });
  };

  const confirmDeleteMaterial = async () => {
    if (!deleteConfirm) return;
    const { materialType, materialKey } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await axiosWithRetry({
        method: 'delete', url: `${API}/admin/materials/${materialType}/${materialKey}`,
        headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      toast.success('Material gelöscht');
      onUpdateMaterials();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleAddMaterial = async () => {
    try {
      await axiosWithRetry({
        method: 'post', url: `${API}/admin/materials`,
        data: newMaterial,
        headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      toast.success('Material hinzugefügt');
      onUpdateMaterials();
      setNewMaterial({
        type: 'filament',
        key: '',
        name: '',
        density: 1.0,
        price_per_gram: 0.1,
        colors: []
      });
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  return (
    <div className="space-y-6">
      {/* Material hinzufügen */}
      <Card className="text-black">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-black">
            <Plus className="w-5 h-5" />
            Neues Material hinzufügen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Material-Typ</Label>
              <select
                className="w-full p-2 border rounded"
                value={newMaterial.type}
                onChange={(e) => setNewMaterial({...newMaterial, type: e.target.value})}
              >
                <option value="filament">Filament</option>
                <option value="resin">Resin</option>
              </select>
            </div>
            <div>
              <Label>Schlüssel (z.B. "pla")</Label>
              <Input
                value={newMaterial.key}
                onChange={(e) => setNewMaterial({...newMaterial, key: e.target.value})}
                placeholder="pla"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Anzeigename</Label>
              <Input
                value={newMaterial.name}
                onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                placeholder="PLA Filament"
              />
            </div>
            <div>
              <Label>Dichte (g/cm³)</Label>
              <Input
                type="number"
                step="0.01"
                value={newMaterial.density}
                onChange={(e) => setNewMaterial({...newMaterial, density: parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <Label>Preis (€/g)</Label>
              <Input
                type="number"
                step="0.001"
                value={newMaterial.price_per_gram}
                onChange={(e) => setNewMaterial({...newMaterial, price_per_gram: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          <div>
            <Label>Farben (kommagetrennt)</Label>
            <Input
              value={newMaterial.colors.join(', ')}
              onChange={(e) => setNewMaterial({...newMaterial, colors: e.target.value.split(', ').filter(c => c.trim())})}
              placeholder="Schwarz, Weiß, Rot"
            />
          </div>
          <Button onClick={handleAddMaterial} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Material hinzufügen
          </Button>
        </CardContent>
      </Card>

      {/* Bestehende Materialien */}
      {materials && Object.entries(materials).map(([type, typeMaterials]) => (
        <Card key={type} className="text-black">
          <CardHeader>
            <CardTitle className="capitalize text-black">
              {type === 'filament' ? 'Filament-Materialien' : 'Resin-Materialien'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(typeMaterials).map(([key, material]) => (
              <div key={key} className="border rounded-lg p-4">
                {editingMaterial === `${type}-${key}` ? (
                  <EditMaterialForm
                    material={material}
                    onSave={(updatedMaterial) => handleSaveMaterial(type, key, updatedMaterial)}
                    onCancel={() => setEditingMaterial(null)}
                  />
                ) : (
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{material.name}</h3>
                        <Badge variant="outline">{key}</Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        Dichte: {material.density} g/cm³ • Preis: €{material.price_per_gram}/g
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {material.colors.map(color => (
                          <Badge key={color} variant="secondary" className="text-xs">
                            {color}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingMaterial(`${type}-${key}`)}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteMaterial(type, key, material.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Material löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Soll <strong>{deleteConfirm?.materialName || deleteConfirm?.materialKey}</strong> wirklich gelöscht werden?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMaterial}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditMaterialForm({ material, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: material.name,
    density: material.density,
    price_per_gram: material.price_per_gram,
    colors: material.colors
  });

  return (
    <div className="space-y-4 text-black">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
          />
        </div>
        <div>
          <Label>Dichte (g/cm³)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.density}
            onChange={(e) => setFormData({...formData, density: parseFloat(e.target.value)})}
          />
        </div>
        <div>
          <Label>Preis (€/g)</Label>
          <Input
            type="number"
            step="0.001"
            value={formData.price_per_gram}
            onChange={(e) => setFormData({...formData, price_per_gram: parseFloat(e.target.value)})}
          />
        </div>
      </div>
      <div>
        <Label>Farben</Label>
        <Input
          value={formData.colors.join(', ')}
          onChange={(e) => setFormData({...formData, colors: e.target.value.split(', ').filter(c => c.trim())})}
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(formData)} className="flex-1">
          <Save className="w-4 h-4 mr-2" />
          Speichern
        </Button>
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Abbrechen
        </Button>
      </div>
    </div>
  );
}

function PrinterLimitsManager({ limits, onUpdateLimits }) {
  const [editingLimits, setEditingLimits] = useState(limits || {});

  useEffect(() => {
    setEditingLimits(limits || {});
  }, [limits]);

  const handleSave = async () => {
    try {
      await axiosWithRetry({
        method: 'put', url: `${API}/admin/printer-limits`,
        data: editingLimits,
        headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` }
      });
      toast.success('Drucker-Limits aktualisiert');
      onUpdateLimits();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <Card className="text-black">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-black">
          <Printer className="w-5 h-5" />
          Drucker-Dimensionslimits
        </CardTitle>
        <CardDescription className="text-gray-700">
          Maximale Druckgrößen für Resin- und Filament-Drucker
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(editingLimits).map(([type, typeLimits]) => (
          <div key={type}>
            <h3 className="font-semibold mb-3 capitalize text-gray-900">
              {type === 'resin' ? 'Resin-Drucker' : 'Filament-Drucker'}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-800">Max X (mm)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={typeLimits.max_x}
                  onChange={(e) => setEditingLimits({
                    ...editingLimits,
                    [type]: {...typeLimits, max_x: parseFloat(e.target.value)}
                  })}
                />
              </div>
              <div>
                <Label className="text-gray-800">Max Y (mm)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={typeLimits.max_y}
                  onChange={(e) => setEditingLimits({
                    ...editingLimits,
                    [type]: {...typeLimits, max_y: parseFloat(e.target.value)}
                  })}
                />
              </div>
              <div>
                <Label className="text-gray-800">Max Z (mm)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={typeLimits.max_z}
                  onChange={(e) => setEditingLimits({
                    ...editingLimits,
                    [type]: {...typeLimits, max_z: parseFloat(e.target.value)}
                  })}
                />
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Aktuell: {typeLimits.max_x} × {typeLimits.max_y} × {typeLimits.max_z} mm
            </div>
          </div>
        ))}
        <Button onClick={handleSave} className="w-full">
          <Save className="w-4 h-4 mr-2" />
          Limits speichern
        </Button>
      </CardContent>
    </Card>
  );
}



function CalculatorManager({ config, onSave }) {
  const [formData, setFormData] = useState(config || {
    energy_price_kwh: 0.20,
    profit_margin_percent: 30,
    material_densities: {
      PLA: 1.24,
      ABS: 1.04,
      PETG: 1.27,
      TPU: 1.21,
      ASA: 1.07,
      NYLON: 1.14
    },
    quality_profiles: {
      draft: { layer_height: 0.3, print_speed_mm_s: 80 },
      normal: { layer_height: 0.2, print_speed_mm_s: 50 },
      quality: { layer_height: 0.12, print_speed_mm_s: 30 },
      fine: { layer_height: 0.08, print_speed_mm_s: 20 }
    },
    shell_thickness_mm: 1.2,
    support_percent: 15,
    support_time_multiplier: 1.3
  });
  
  const [saving, setSaving] = useState(false);

  // Update formData when config is loaded from backend
  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosWithRetry({
        method: 'put', url: `${API}/admin/calculator`,
        data: formData,
        headers: { Authorization: 'Bearer ' + sessionStorage.getItem('adminToken') }
      });
      toast.success('Kalkulator-Einstellungen gespeichert');
      onSave && onSave();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Fehler beim Speichern:', error);
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Grundeinstellungen */}
      <Card className="bg-slate-700 border-slate-600">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <DollarSign className="w-5 h-5" />
            Grundeinstellungen
          </CardTitle>
          <CardDescription className="text-gray-700">Energie- und Gewinneinstellungen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="energy_price" className="text-gray-200">Strompreis (€/kWh)</Label>
              <Input
                id="energy_price"
                type="number"
                step="0.01"
                min="0"
                value={formData.energy_price_kwh || 0}
                onChange={(e) => handleInputChange('energy_price_kwh', parseFloat(e.target.value))}
                className="bg-slate-600 border-slate-500 text-white"
              />
            </div>
            <div>
              <Label htmlFor="profit_margin" className="text-gray-200">Gewinnaufschlag (%)</Label>
              <Input
                id="profit_margin"
                type="number"
                step="1"
                min="0"
                value={formData.profit_margin_percent || 0}
                onChange={(e) => handleInputChange('profit_margin_percent', parseFloat(e.target.value))}
                className="bg-slate-600 border-slate-500 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Material-Dichten */}
      <Card className="bg-slate-700 border-slate-600">
        <CardHeader>
          <CardTitle className="text-white">Material-Dichten (g/cm³)</CardTitle>
          <CardDescription className="text-gray-700">Dichte für Gewichtsberechnung</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            {Object.keys(formData.material_densities || {}).map(material => (
              <div key={material}>
                <Label htmlFor={`density_${material}`} className="text-gray-200">{material}</Label>
                <Input
                  id={`density_${material}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.material_densities[material] || 0}
                  onChange={(e) => handleInputChange(`material_densities.${material}`, parseFloat(e.target.value))}
                  className="bg-slate-600 border-slate-500 text-white"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Qualitäts-Profile */}
      <Card className="bg-slate-700 border-slate-600">
        <CardHeader>
          <CardTitle className="text-white">Qualitäts-Profile</CardTitle>
          <CardDescription className="text-gray-700">Schichthöhe und Druckgeschwindigkeit</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.keys(formData.quality_profiles || {}).map(quality => (
            <div key={quality} className="border-t border-slate-600 pt-4 first:border-t-0 first:pt-0">
              <h4 className="font-semibold text-gray-200 mb-3 capitalize">{quality}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`layer_${quality}`} className="text-gray-200">Schichthöhe (mm)</Label>
                  <Input
                    id={`layer_${quality}`}
                    type="number"
                    step="0.01"
                    min="0.08"
                    max="0.4"
                    value={formData.quality_profiles[quality].layer_height || 0}
                    onChange={(e) => handleInputChange(`quality_profiles.${quality}.layer_height`, parseFloat(e.target.value))}
                    className="bg-slate-600 border-slate-500 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor={`speed_${quality}`} className="text-gray-200">Druckgeschwindigkeit (mm/s)</Label>
                  <Input
                    id={`speed_${quality}`}
                    type="number"
                    step="1"
                    min="10"
                    value={formData.quality_profiles[quality].print_speed_mm_s || 0}
                    onChange={(e) => handleInputChange(`quality_profiles.${quality}.print_speed_mm_s`, parseFloat(e.target.value))}
                    className="bg-slate-600 border-slate-500 text-white"
                  />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Druck-Parameter */}
      <Card className="bg-slate-700 border-slate-600">
        <CardHeader>
          <CardTitle className="text-white">Druck-Parameter</CardTitle>
          <CardDescription className="text-gray-700">Support und Wandstärke Einstellungen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="shell_thickness" className="text-gray-200">Wandstärke (mm)</Label>
              <Input
                id="shell_thickness"
                type="number"
                step="0.1"
                min="0.5"
                value={formData.shell_thickness_mm || 0}
                onChange={(e) => handleInputChange('shell_thickness_mm', parseFloat(e.target.value))}
                className="bg-slate-600 border-slate-500 text-white"
              />
            </div>
            <div>
              <Label htmlFor="support_percent" className="text-gray-200">Support Prozent (%)</Label>
              <Input
                id="support_percent"
                type="number"
                step="1"
                min="0"
                max="100"
                value={formData.support_percent || 0}
                onChange={(e) => handleInputChange('support_percent', parseFloat(e.target.value))}
                className="bg-slate-600 border-slate-500 text-white"
              />
            </div>
            <div>
              <Label htmlFor="support_multiplier" className="text-gray-200">Support Multiplikator</Label>
              <Input
                id="support_multiplier"
                type="number"
                step="0.1"
                min="0.5"
                value={formData.support_time_multiplier || 0}
                onChange={(e) => handleInputChange('support_time_multiplier', parseFloat(e.target.value))}
                className="bg-slate-600 border-slate-500 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Berechnung Info */}
      <Card className="bg-blue-950 border-blue-700">
        <CardHeader>
          <CardTitle className="text-blue-200">Berechnungs-Formel</CardTitle>
        </CardHeader>
        <CardContent className="text-blue-100 text-sm space-y-2">
          <p><strong>Filament-Gewicht:</strong> (Modell-Vol + Support-Vol) × Material-Dichte</p>
          <p><strong>Druckzeit:</strong> Volumen ÷ (Schichthöhe × Druckgeschwindigkeit × Breite)</p>
          <p><strong>Support-Volumen:</strong> Modell-Vol × (Support % ÷ 100)</p>
          <p><strong>Support-Zeit:</strong> Basis-Druckzeit × Support-Multiplikator × 0.3</p>
          <p><strong>Kosten:</strong> (Material-Kosten + Druck-Kosten + Support-Kosten) × (1 + Gewinnaufschlag)</p>
        </CardContent>
      </Card>

      {/* Speichern Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-green-600 hover:bg-green-700 text-white py-2"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
      </Button>
    </div>
  );
}


function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [materials, setMaterials] = useState(null);
  const [printerLimits, setPrinterLimits] = useState(null);
  const [calculatorConfig, setCalculatorConfig] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = sessionStorage.getItem('adminToken');
      const savedUserInfo = sessionStorage.getItem('adminUserInfo');
      
      // First, try auto-login with Odoo session
      if (!token) {
        try {
          const response = await axiosWithRetry({
            method: 'get', url: `${API}/admin/auto-login`,
            withCredentials: true
          });
          if (response.data.success && response.data.token) {
            sessionStorage.setItem('adminToken', response.data.token);
            sessionStorage.setItem('adminUserInfo', JSON.stringify(response.data.user_info));
            setUserInfo(response.data.user_info);
            setIsAuthenticated(true);
            await loadAdminData();
            setLoading(false);
            return;
          }
        } catch (error) {
          if (process.env.NODE_ENV !== "production") console.log('Auto-login fehlgeschlagen, zeige Login-Form');
        }
      }
      
      // Falls Auto-Login fehlschlägt oder Token existiert, verifiziere Token
      if (token) {
        if (savedUserInfo) {
          setUserInfo(JSON.parse(savedUserInfo));
        }
        verifyToken(token);
      } else {
        setLoading(false);
      }
    };
    
    initializeAuth();
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axiosWithRetry({
        method: 'get', url: `${API}/admin/verify`,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUserInfo(response.data.user_info);
        sessionStorage.setItem('adminUserInfo', JSON.stringify(response.data.user_info));
        loadAdminData();
        setLoading(false);
        return;
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Token verification error:', error);
    }
    // Token ungültig oder abgelaufen – sessionStorage leeren und Auto-Login versuchen
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUserInfo');
    try {
      const autoRes = await axiosWithRetry({ method: 'get', url: `${API}/admin/auto-login`, withCredentials: true });
      if (autoRes.data.success && autoRes.data.token) {
        sessionStorage.setItem('adminToken', autoRes.data.token);
        sessionStorage.setItem('adminUserInfo', JSON.stringify(autoRes.data.user_info));
        setUserInfo(autoRes.data.user_info);
        setIsAuthenticated(true);
        await loadAdminData();
        setLoading(false);
        return;
      }
    } catch (autoError) {
      if (process.env.NODE_ENV !== "production") console.log('Auto-login nach Token-Fehler fehlgeschlagen');
    }
    setLoading(false);
  };

  const loadAdminData = async () => {
    try {
      const [materialsRes, limitsRes, calculatorRes, quotesRes] = await Promise.all([
        axiosWithRetry({ method: 'get', url: `${API}/materials` }),
        axiosWithRetry({ method: 'get', url: `${API}/printer-limits` }),
        axiosWithRetry({
          method: 'get', url: `${API}/admin/calculator`,
          headers: { Authorization: 'Bearer ' + sessionStorage.getItem('adminToken') }
        }),
        axiosWithRetry({
          method: 'get', url: `${API}/admin/quotes`,
          headers: { Authorization: 'Bearer ' + sessionStorage.getItem('adminToken') }
        })
      ]);
      setMaterials(materialsRes.data);
      setPrinterLimits(limitsRes.data);
      setCalculatorConfig(calculatorRes.data);
      setQuotes(quotesRes.data || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error loading admin data:', error);
      toast.error('Fehler beim Laden der Daten');
    }
  };

  const handleLoginSuccess = async (token, loginUserInfo) => {
    if (loginUserInfo) {
      setUserInfo(loginUserInfo);
    }
    setIsAuthenticated(true);
    setLoading(true);
    await loadAdminData();
    setLoading(false);
  };


  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUserInfo');
    setIsAuthenticated(false);
    setMaterials(null);
    setPrinterLimits(null);
    setQuotes([]);
    setUserInfo(null);
    toast.success('Erfolgreich abgemeldet');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white">Lade...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-black bg-opacity-50 border-b border-gray-700">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Settings className="w-8 h-8 text-red-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">3D-Druck Admin-Panel</h1>
              <p className="text-gray-300 text-sm">Preise und Materialien verwalten</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {userInfo && (
              <div className="text-white text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="destructive">Admin</Badge>
                  <span>{userInfo.name || userInfo.username}</span>
                </div>
                <div className="text-xs text-gray-300">
                  {userInfo.auth_method === 'api_key' ? 'API-Schlüssel' : 'Passwort'} • 
                  {userInfo.groups?.length || 0} Gruppe(n)
                </div>
              </div>
            )}
            <Button variant="outline" onClick={handleLogout} className="text-white border-gray-600">
              Abmelden
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="materials" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="materials" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Materialien & Preise
            </TabsTrigger>
            <TabsTrigger value="calculator" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Kalkulator
            </TabsTrigger>
            <TabsTrigger value="limits" className="flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Drucker-Limits
            </TabsTrigger>
            <TabsTrigger value="quotes" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Angebote
            </TabsTrigger>
          </TabsList>

          <TabsContent value="materials">
            <MaterialsManager 
              materials={materials} 
              onUpdateMaterials={loadAdminData}
            />
          </TabsContent>

          <TabsContent value="calculator">
            <CalculatorManager 
              config={calculatorConfig} 
              onSave={loadAdminData}
            />
          </TabsContent>

          <TabsContent value="limits">
            <PrinterLimitsManager 
              limits={printerLimits} 
              onUpdateLimits={loadAdminData}
            />
          </TabsContent>

          <TabsContent value="quotes">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Angebotshistorie</h2>
                <span className="text-sm text-gray-400">{quotes.length} Angebot{quotes.length !== 1 ? 'e' : ''}</span>
              </div>
              {quotes.length === 0 ? (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-8 text-center text-gray-400">
                    Noch keine Angebote vorhanden.
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-700 text-gray-400">
                          <th className="text-left p-3 font-medium">Datum</th>
                          <th className="text-left p-3 font-medium">Material</th>
                          <th className="text-left p-3 font-medium">Farbe</th>
                          <th className="text-right p-3 font-medium">Druckzeit</th>
                          <th className="text-right p-3 font-medium">Gewicht</th>
                          <th className="text-right p-3 font-medium">Preis</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotes.map((q, i) => (
                          <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                            <td className="p-3 text-gray-300 whitespace-nowrap text-xs">
                              {new Date(q.timestamp).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                            </td>
                            <td className="p-3 text-gray-300">{q.material}</td>
                            <td className="p-3 text-gray-300">{q.color}</td>
                            <td className="p-3 text-gray-300 text-right whitespace-nowrap">{q.print_time_hours?.toFixed(1)} h</td>
                            <td className="p-3 text-gray-300 text-right whitespace-nowrap">{q.scaled_weight?.toFixed(1)} g</td>
                            <td className="p-3 text-green-400 font-semibold text-right whitespace-nowrap">€{q.price?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Security Notice */}
      <div className="fixed bottom-4 right-4">
        <Alert className="bg-red-900 border-red-700 text-white">
          <Lock className="h-4 w-4" />
          <AlertDescription>
            Sicher: Odoo-Authentifizierung aktiv
            {userInfo && (
              <div className="text-xs mt-1">
                Angemeldet: {userInfo.username} ({userInfo.auth_method})
              </div>
            )}
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}

export default AdminPanel;
